'use client'

import { useLocale, useTranslations } from 'next-intl'
import { useState } from 'react'

import { UserType } from '@/entities/user'
import { LocaleSelector } from '@/features/change-locale'
import { useAirbridgeContext } from '@/shared/config'
import { Link } from '@/shared/i18n'
import { cn, useScroll } from '@/shared/lib'
import { TextButton, Button, Separator, Icon, MobileLayout } from '@/shared/ui'

import Blog from '../asset/blog.svg'
import Instagram from '../asset/instagram.svg'
import YouTube from '../asset/youtube.svg'

type Props = {
  pageType: UserType
  className?: string
}

export const MobileFooter = ({ pageType, className }: Props) => {
  const locale = useLocale()
  const t = useTranslations('footer')
  const airbridge = useAirbridgeContext()

  const [isOpenBusinessInfo, setIsOpenBusinessInfo] = useState(false)
  const { ref: businessInfoContentRef, scrollIntoView } =
    useScroll<HTMLButtonElement>()

  const handleOpenDeeplink = () => {
    airbridge.openDeeplink({
      ctaParams: {
        cta_param_1: 'footer_inquiry',
        cta_param_2: '',
        cta_param_3: '',
      },
    })
  }

  const handleOpenBusinessInfo = () => {
    const willExpand = !isOpenBusinessInfo

    setIsOpenBusinessInfo(willExpand)

    if (willExpand) {
      scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
  }

  return (
    <footer className={cn('bg-white', className)}>
      <MobileLayout className="space-y-6">
        <LocaleSelector isMobile locale={locale} className="mb-7.5" />

        <ul className="mb-7.5 flex gap-4">
          <li>
            <TextButton weight="normal" size="small" underline asChild>
              <Link href={`/${pageType}/notice`}>{t('notice')}</Link>
            </TextButton>
          </li>
          <li>
            <TextButton weight="normal" size="small" underline asChild>
              <Link href="/host/support/faq" locale="ko">
                {t('landlord_faq')}
              </Link>
            </TextButton>
          </li>
          <li>
            <TextButton weight="normal" size="small" underline asChild>
              <Link href="/guest/support/faq">{t('tenant_faq')}</Link>
            </TextButton>
          </li>
        </ul>

        <div>
          <h4 className="typo-label2 mb-1.5 font-semibold text-black">
            {t('customer_center.title')}
          </h4>
          <ul
            className={cn(
              'typo-caption1 mb-1.5 flex space-x-1 font-normal text-gray-600',
              '*:flex',
              '*:not-last:after:ml-1 *:not-last:after:block *:not-last:after:text-gray-200 *:not-last:after:content-["ㅣ"]',
            )}
          >
            <li>
              <p>{t('customer_center.description.operating_hours')}</p>
            </li>
            <li>
              <p>{t('customer_center.description.phone_number')}</p>
            </li>
          </ul>
          <p className="typo-caption1 mb-2 font-normal text-gray-500">
            {t('customer_center.description.line2')}
          </p>
          <Button
            variant="outline"
            size="small"
            className="min-w-[101px]"
            onClick={handleOpenDeeplink}
          >
            {t('button.inquiry')}
            <Icon name="arrow-right" size={16} className="text-black" />
          </Button>
        </div>

        <div>
          <button
            ref={businessInfoContentRef}
            className="flex w-full items-center justify-between"
            onClick={handleOpenBusinessInfo}
            aria-expanded={isOpenBusinessInfo}
          >
            <h4 className="typo-label2 font-semibold text-black">
              {t('business_info.title')}
            </h4>
            <Icon
              name="arrow-bottom"
              size={18}
              className={cn('text-black', isOpenBusinessInfo && 'rotate-180')}
            />
          </button>
          {isOpenBusinessInfo && (
            <p className={cn('typo-caption1 mt-1 text-gray-600')}>
              {t('business_info.company_name')}
              <br />
              {t('business_info.ceo_name')}
              <br />
              {t('business_info.business_number')}
              <br />
              {t('business_info.mail_order_number')}
              <br />
              {t('business_info.address')}
              <br />
              {t('business_info.email')}
              <br />
              {t('business_info.partnership')}
              <br />
              {t('business_info.hosting_service')}
            </p>
          )}
          <ul className="mt-2 flex gap-4">
            <li className="h-4 leading-4">
              <TextButton
                color="gray"
                size="small"
                weight="normal"
                underline
                asChild
              >
                <Link href="/terms/using">{t('terms')}</Link>
              </TextButton>
            </li>
            <li className="h-4 leading-4">
              <TextButton
                color="gray"
                size="small"
                weight="normal"
                underline
                asChild
              >
                <Link href="/terms/privacy-policy">{t('privacy_policy')}</Link>
              </TextButton>
            </li>
          </ul>
        </div>

        <ul className="flex gap-3.5">
          <li>
            <Link
              href="https://blog.naver.com/33m2_app"
              className="flex h-8 w-8 items-center justify-center"
              aria-label="33m2 블로그로 이동"
              target="_blank"
              rel="noopener noreferrer"
            >
              <Blog />
            </Link>
          </li>
          <li>
            <Link
              href="https://www.instagram.com/33m2.co.kr"
              className="flex h-8 w-8 items-center justify-center"
              aria-label="33m2 인스타그램으로 이동"
              target="_blank"
              rel="noopener noreferrer"
            >
              <Instagram />
            </Link>
          </li>
          <li>
            <Link
              href="https://www.youtube.com/@33m2.official"
              className="flex h-8 w-8 items-center justify-center"
              aria-label="33m2 유튜브로 이동"
              target="_blank"
              rel="noopener noreferrer"
            >
              <YouTube />
            </Link>
          </li>
        </ul>

        <Separator />

        <p className="typo-caption2 font-normal text-gray-500">
          {t('disclaimer')}
        </p>
      </MobileLayout>
    </footer>
  )
}
